import { logger } from '@vagabondinternet/logging';
import { authErrorCode, authFailure } from './auth-errors';
import { loadEmailAuth } from './load-email-auth';
import type { SignInLinkEmailStore } from './sign-in-link-email';
import type { SignInResult } from './sign-in-with-email';

export interface SignInLinkOptions {
  /** Where the email's link continues to: id.<domain>/sign-in/link, keeping a valid return_to. */
  continueUrl: string;
}

/** Identity Platform's email link sign-in; each call rejects with the Firebase error. */
export interface EmailLinkSignIn {
  /** Emails a one-time sign-in link to `email` (sendSignInLinkToEmail, handled in the app). */
  sendLink: (email: string, options: SignInLinkOptions) => Promise<void>;
  /** Whether `link` is a sign-in link: it carries oobCode, apiKey and mode=signIn (isSignInWithEmailLink). */
  isSignInLink: (link: string) => Promise<boolean>;
  /** Signs in with the link for `email`, using it up. Identity Platform marks the address verified. */
  signInWithLink: (email: string, link: string) => Promise<SignInResult>;
}

/** Identity Platform email link sign-in (email-auth.ts), loading the Firebase SDK on first use. */
export const identityPlatformEmailLink: EmailLinkSignIn = {
  sendLink: async (email, options) => (await loadEmailAuth()).emailLink.sendLink(email, options),
  isSignInLink: async (link) => (await loadEmailAuth()).emailLink.isSignInLink(link),
  signInWithLink: async (email, link) => (await loadEmailAuth()).emailLink.signInWithLink(email, link),
};

/**
 * What a sign-in link turned out to be when it can't be used. `wrong-email` is a link for another address than the
 * one given; `not-allowed` is a link the pre-launch allowlist refuses, for a new or an existing account alike.
 */
export type SignInLinkFailure = 'expired' | 'invalid' | 'wrong-email' | 'not-allowed';

/**
 * What a failed signInWithEmailLink means for the link. A used code is invalid like a malformed one
 * (auth/invalid-action-code), and a disabled account is invalid too, so nothing is revealed about the account.
 * Network and rate-limit failures are undefined: the link may still be good, so the player can try again.
 */
export const signInLinkFailure = (error: unknown): SignInLinkFailure | undefined => {
  const failure = authFailure(error);
  if (failure === 'network' || failure === 'too-many-requests') {
    return undefined;
  }
  if (failure === 'not-allowed') {
    return 'not-allowed';
  }
  switch (authErrorCode(error)) {
    case 'auth/expired-action-code':
      return 'expired';
    case 'auth/invalid-email':
      return 'wrong-email';
    default:
      return 'invalid';
  }
};

/**
 * Sends a sign-in link and remembers the address on this device, so opening the link here needs no typing. Rejects
 * only when the player can do something about it: too many requests or a network failure. Any other refusal resolves
 * like a sent link, so the page never reveals whether an account exists; it is logged by code only.
 */
export const sendSignInLinkQuietly = async (
  emailLink: EmailLinkSignIn,
  emailStore: SignInLinkEmailStore,
  email: string,
  continueUrl: string,
): Promise<void> => {
  try {
    await emailLink.sendLink(email, { continueUrl });
  } catch (error) {
    const failure = authFailure(error);
    if (failure === 'network' || failure === 'too-many-requests') {
      throw error;
    }
    logger.error({ message: 'Sign-in link refused', code: authErrorCode(error) });
  }
  emailStore.save(email);
};
